import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signup } from '../../services/signUpService';
import { getGaContext } from '../../services/ga4Client';
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from '../../components/auth/TurnstileWidget';
import { recordLegalAcceptance, LEGAL_VERSION } from '../legal/legalContent';
import { authErrorMessage, authMessage } from '../../i18n/authMessages';
import { detectLanguage } from '../../i18n/language';
import LogoIconSrc from './../../assets/icons/logo/pixle-transparent-inverted.png';
import './signup.css';
import { toast } from 'react-toastify';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [studioName, setStudioName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const language = detectLanguage();
    if (!acceptedTerms) {
      toast.error(authMessage('signup.acceptTerms', language));
      return;
    }
    if (!turnstileToken) {
      toast.error(authMessage('signup.completeVerification', language));
      return;
    }
    setLoading(true);

    try {
      const gaContext = await getGaContext();
      const response = await signup({
        email,
        password,
        firstName: firstName,
        lastName: lastName,
        studioName: studioName,
        acceptedTermsVersion: LEGAL_VERSION,
        'cf-turnstile-response': turnstileToken,
        ...(gaContext.clientId
          ? {
              analytics: {
                clientId: gaContext.clientId,
                ...(gaContext.sessionId
                  ? { sessionId: gaContext.sessionId }
                  : {}),
              },
            }
          : {}),
      });

      toast.success(authMessage('signup.success', language));
      toast.warn(authMessage('signup.checkEmail', language));

      recordLegalAcceptance();
      if (response.user) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error: any) {
      toast.error(authErrorMessage(error, 'signup.failed', language));
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="auth-hero">
        <p className="auth-hero-title">
          Pixle — an AI-native animation studio built for creators
        </p>
        <ul className="auth-hero-list">
          <li>Your creative IP remains yours</li>
          <li>Secure payments powered by Stripe</li>
          <li>Access leading AI models in one studio</li>
          <li>Create production-ready animation in minutes</li>
        </ul>
      </div>
      <div className="signup-right">
        <img src={LogoIconSrc} alt="Logo Icon" />
        <h2 className="signup-title">Start creating with Pixle</h2>
        <p className="signup-subtitle">
          Your first generations are on us. No card required.
        </p>

        <form className="signup-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Type your first name here"
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Type your last name here"
            />
          </div>

          <div className="form-group">
            <label>Email address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Type your email address here"
              required
            />
          </div>

          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Type your password here"
              required
            />
          </div>

          <div className="form-group">
            <label>Studio Name/Channel Name</label>
            <input
              type="text"
              value={studioName}
              onChange={(e) => setStudioName(e.target.value)}
              placeholder="Type your studio/channel name here"
            />
          </div>

          <div className="signup-controls">
            <label className="signup-terms">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
              />
              <span>
                I agree to the <Link to="/terms" target="_blank">Terms of Service</Link>{' '}
                and <Link to="/privacy" target="_blank">Privacy Policy</Link>
              </span>
            </label>
            <TurnstileWidget
              ref={turnstileRef}
              onTokenChange={setTurnstileToken}
              className="turnstile-container"
            />
            <button
              type="submit"
              className="signup-button"
              disabled={loading || !acceptedTerms || !turnstileToken}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
            <p className="register-text">
              Already have an account? <a href="/login">Login</a>
            </p>
          </div>
        </form>
        <p className="signup-founder-note">
          Building Pixle with creators.
          <br />
          Have feedback?{' '}
          <a
            href="https://www.linkedin.com/in/priyanka-jhaveri-480bbb27a/"
            target="_blank"
            rel="noreferrer"
          >
            Talk to the founder →
          </a>
        </p>
      </div>
    </div>
  );
};

export default SignUpPage;
